/**
 * AgionAssistant — Global floating AI chat panel.
 * i18n namespace: components:assistant
 *
 * Two sizes: compact (default) and expanded (toggle via header button).
 * Compact: 420×600 — quick interactions.
 * Expanded: 560×85vh — extended conversations, more context visible.
 *
 * Open via:
 * - Click the floating button
 * - Cmd+K / Ctrl+K keyboard shortcut (handled in MainLayout)
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { X, Maximize2, Minimize2, Sparkles, ArrowUpRight, FileText, Users, Shield, Server, Tag, LayoutGrid } from 'lucide-react'
import { Button, Text, ChatInput, ActivityIndicator } from '@agion/ui'
import { cn } from '@/shared/lib/utils'
import AgionIcon from '@/assets/agion-icon.svg'
import AgionIconDark from '@/assets/agion-icon-dark.svg'
import { useAssistant } from './useAssistant'
import type { AssistantMessage } from './useAssistant'
import { ChatRulePreview } from './ChatRulePreview'
import type { GovernanceLabel, LabelCategory } from '@/types/governance-labels'
import { useAssistantStore } from '@/shared/stores'
import { useEntityIndex, type IndexedEntity, type EntityType } from '@/hooks/useEntityIndex'
import { trackSearchClick } from '@/lib/telemetry'

/**
 * Tiny markdown→HTML renderer for chat bubbles.
 *
 * Supported:
 *   **bold**, `code`, `- list`, `---` rule, [link text](url), and
 *   github-style tables (| header | … | + |---|---|).
 *
 * Pre-escapes `&` and `<` so injected text can't break the bubble.
 * Exported (cycle 3) so the Slice B retrieve renderer's output —
 * markdown tables for rows + `[open](admin_url)` links — can be
 * unit-tested without mounting the full React tree.
 */
export function simpleMarkdown(text: string): string {
  // Escape first so subsequent regex inserts the right HTML.
  let out = text.replace(/&/g, '&amp;').replace(/</g, '&lt;')

  // Render github-style tables BEFORE we touch newlines: gather
  // contiguous `| col | col |` lines + a `|---|---|` separator into
  // a <table>. Lines that aren't part of a table fall through.
  out = renderTables(out)

  // Inline elements.
  out = out
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-xs font-mono">$1</code>')
    // [text](url) — open admin pages from atoms / navigation hits.
    // Conservative: only http(s) and absolute /paths.
    .replace(
      /\[([^\]]+)\]\((\/[^)\s]*|https?:\/\/[^)\s]*)\)/g,
      '<a href="$2" class="text-blue-600 dark:text-blue-400 underline">$1</a>',
    )
    .replace(/^- (.+)$/gm, '• $1')
    .replace(/^---$/gm, '<hr class="my-2 border-slate-200 dark:border-slate-700" />')

  // Newlines last so table HTML doesn't get <br /> between rows.
  // Tables already rendered above are wrapped in a sentinel that we
  // restore afterward.
  out = out.replace(/\n/g, '<br />')
  return out.replace(/§§TBL§§/g, '\n')
}

function renderTables(text: string): string {
  const lines = text.split('\n')
  const out: string[] = []
  let i = 0
  while (i < lines.length) {
    const header = lines[i]
    const sep = lines[i + 1]
    if (
      header && sep &&
      /^\|.+\|$/.test(header.trim()) &&
      /^\|[\s\-|]+\|?$/.test(sep.trim())
    ) {
      // Collect data rows until a non-table line.
      const cells = (s: string) => s.replace(/^\||\|$/g, '').split('|').map(c => c.trim())
      const headerCells = cells(header)
      const rows: string[][] = []
      let j = i + 2
      while (j < lines.length) {
        const rowLine = lines[j]
        if (!rowLine || !/^\|.+\|?$/.test(rowLine.trim())) break
        rows.push(cells(rowLine))
        j++
      }
      // Build inline-styled HTML so the chat bubble doesn't need extra CSS.
      const th = headerCells.map(c => `<th class="px-2 py-1 text-left">${c}</th>`).join('')
      const tr = rows.map(
        r => `<tr>${r.map(c => `<td class="px-2 py-1 border-t border-slate-200 dark:border-slate-700">${c}</td>`).join('')}</tr>`,
      ).join('')
      out.push(
        `<table class="my-2 text-xs border-collapse">§§TBL§§<thead><tr>${th}</tr></thead>§§TBL§§<tbody>${tr}</tbody>§§TBL§§</table>`,
      )
      i = j
      continue
    }
    out.push(header ?? '')
    i++
  }
  return out.join('\n')
}

export function AgionAssistant() {
  const { t } = useTranslation('components')
  const navigate = useNavigate()
  const { isOpen, open, close } = useAssistantStore()
  const [isExpanded, setIsExpanded] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const { messages, isLoading, isModelConnected, sendMessage, executeAction, labelCatalog, stop } = useAssistant()

  // Hybrid search: instant navigational suggestions while typing
  const navigationalResults = useEntityIndex(inputValue)

  const handleSubmit = useCallback((text: string) => {
    setInputValue('')
    sendMessage(text)
  }, [sendMessage])

  const handleNavigationalClick = useCallback((entity: IndexedEntity, rank: number) => {
    // Track the click
    trackSearchClick(inputValue, entity.id, rank, entity.type)
    // Navigate to the entity
    navigate(entity.route)
    // Clear input and close suggestions
    setInputValue('')
  }, [inputValue, navigate])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // --- Floating button (closed state) ---
  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50 group">
        <Button
          variant="ghost"
          onClick={open}
          className={cn(
            'w-14 h-14 rounded-full shadow-lg p-0',
            'bg-white dark:bg-slate-800',
            'hover:shadow-xl hover:bg-white dark:hover:bg-slate-800',
            'transition-all duration-200 hover:scale-105 active:scale-95',
            'flex items-center justify-center',
            'border border-slate-200 dark:border-slate-700',
          )}
          aria-label={t('assistant.actions.openAssistant')}
        >
          <img src={AgionIcon} alt="" className="w-8 h-8 dark:hidden" />
          <img src={AgionIconDark} alt="" className="w-8 h-8 hidden dark:block" />
          {isLoading && (
            <span className="absolute top-0 right-0 w-3 h-3 rounded-full bg-amber-500 animate-pulse border-2 border-white dark:border-slate-800" />
          )}
        </Button>
        {/* Keyboard shortcut hint - visible on hover */}
        <div className={cn(
          'absolute bottom-full right-0 mb-2 px-2 py-1 rounded-md',
          'bg-slate-900 dark:bg-slate-700 text-white text-xs whitespace-nowrap',
          'opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none',
        )}>
          {t('assistant.shortcut.hint', 'or press')}{' '}
          <kbd className="px-1 py-0.5 bg-slate-700 dark:bg-slate-600 rounded text-[10px] font-mono">
            {/* eslint-disable-next-line i18next/no-literal-string -- keyboard shortcut symbols are universal */}
            {navigator.platform.includes('Mac') ? '\u2318+K' : 'Ctrl+K'}
          </kbd>
        </div>
      </div>
    )
  }

  // --- Expanded chat panel ---
  const panelClass = isExpanded
    ? 'w-[560px] max-h-[85vh]'
    : 'w-[420px] max-h-[600px]'

  const messagesClass = isExpanded
    ? 'min-h-[300px] max-h-[calc(85vh-130px)]'
    : 'min-h-[200px] max-h-[420px]'

  return (
    <div className={cn(
      'fixed bottom-6 right-6 z-50',
      panelClass,
      'bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700',
      'rounded-2xl shadow-2xl flex flex-col',
      'transition-all duration-200 ease-out',
      'animate-in slide-in-from-bottom-4 fade-in duration-200',
    )}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--border)]">
        <img src={AgionIcon} alt="" className="w-6 h-6 dark:hidden flex-shrink-0" />
        <img src={AgionIconDark} alt="" className="w-6 h-6 hidden dark:block flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <Text className="font-semibold text-sm">{t('assistant.title')}</Text>
          <Text className="text-xs text-[var(--muted-foreground)]">
            {isLoading ? t('assistant.thinking') : t('assistant.subtitle')}
          </Text>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="h-7 w-7 p-0"
            aria-label={isExpanded ? t('assistant.actions.minimize') : t('assistant.actions.expand', 'Expand')}
          >
            {isExpanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={close}
            className="h-7 w-7 p-0"
            aria-label={t('assistant.actions.close')}
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className={cn('flex-1 overflow-y-auto px-4 py-3 space-y-4', messagesClass)}>
        {isModelConnected === false && messages.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
            <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-4">
              <Sparkles className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <Text className="font-semibold mb-2">{t('assistant.emptyState.title')}</Text>
            <Text className="text-sm text-[var(--muted-foreground)] mb-4">{t('assistant.emptyState.description')}</Text>
            <Button variant="default" size="sm" onClick={() => navigate('/governance/resources/providers')} className="gap-2">
              <ArrowUpRight className="w-4 h-4" />
              {t('assistant.emptyState.connectButton')}
            </Button>
          </div>
        )}

        {isModelConnected !== false && messages.length === 0 && !isLoading && (
          <div className="py-4 px-1">
            <div className="text-sm text-[var(--muted-foreground)] space-y-2">
              <p className="font-medium text-[var(--foreground)]">{t('assistant.welcome.greeting')}</p>
              <ul className="space-y-1 ml-1">
                {/* eslint-disable-next-line i18next/no-literal-string -- bullet point symbol */}
                <li>{'\u2022'} {t('assistant.welcome.governance')}</li>
                {/* eslint-disable-next-line i18next/no-literal-string -- bullet point symbol */}
                <li>{'\u2022'} {t('assistant.welcome.missions')}</li>
                {/* eslint-disable-next-line i18next/no-literal-string -- bullet point symbol */}
                <li>{'\u2022'} {t('assistant.welcome.resources')}</li>
                {/* eslint-disable-next-line i18next/no-literal-string -- bullet point symbol */}
                <li>{'\u2022'} {t('assistant.welcome.admin')}</li>
              </ul>
              <p className="text-xs pt-2">{t('assistant.welcome.prompt')}</p>
            </div>
          </div>
        )}

        {messages.map(msg => (
          <MessageRenderer key={msg.id} message={msg} onConfirmAction={executeAction} labelCatalog={labelCatalog} />
        ))}

        {isLoading && (
          <div className="flex items-center gap-2 py-1">
            <ActivityIndicator
              actor="agent"
              name={t('assistant.name', 'Assistant')}
              activity={t('assistant.thinking')}
              isLast
            />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Navigational suggestions - show when typing */}
      {navigationalResults.length > 0 && !isLoading && (
        <div className="border-t border-[var(--border)] bg-slate-50 dark:bg-slate-800/50">
          <div className="px-3 py-2">
            <Text className="text-xs text-[var(--muted-foreground)] mb-1">
              {t('assistant.suggestions.quickNav', 'Quick navigation')}
            </Text>
            <div className="space-y-1">
              {navigationalResults.slice(0, 5).map((entity, index) => (
                <Button
                  key={entity.id}
                  variant="ghost"
                  onClick={() => handleNavigationalClick(entity, index + 1)}
                  className={cn(
                    'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left justify-start h-auto',
                    'hover:bg-slate-100 dark:hover:bg-slate-700/50',
                    'text-sm text-[var(--foreground)]'
                  )}
                >
                  <EntityIcon type={entity.type} />
                  <span className="flex-1 truncate">{entity.name}</span>
                  <span className="text-xs text-[var(--muted-foreground)] capitalize">{entity.type}</span>
                  {entity.status && (
                    <span className={cn(
                      'text-xs px-1.5 py-0.5 rounded-full',
                      entity.status === 'active' || entity.status === 'enabled' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                      entity.status === 'running' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                      'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                    )}>
                      {entity.status}
                    </span>
                  )}
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="px-3 pb-3 pt-1 border-t border-[var(--border)]">
        <ChatInput
          value={inputValue}
          onChange={setInputValue}
          onSubmit={handleSubmit}
          isLoading={isLoading}
          onStop={stop}
          placeholder={t('assistant.placeholder')}
          maxLength={500}
        />
      </div>
    </div>
  )
}

/** Icon component for entity types */
function EntityIcon({ type }: { type: EntityType }) {
  const iconClass = 'w-4 h-4 text-[var(--muted-foreground)]'
  switch (type) {
    case 'mission':
      return <FileText className={iconClass} />
    case 'agent':
      return <Users className={iconClass} />
    case 'rule':
      return <Shield className={iconClass} />
    case 'host':
      return <Server className={iconClass} />
    case 'label':
      return <Tag className={iconClass} />
    case 'page':
      return <LayoutGrid className={iconClass} />
    default:
      return <FileText className={iconClass} />
  }
}

function MessageRenderer({ message, onConfirmAction, labelCatalog }: {
  message: AssistantMessage
  onConfirmAction?: (tool: string, args: Record<string, unknown>) => void
  labelCatalog?: { labels: GovernanceLabel[]; categories: LabelCategory[] } | null
}) {
  const { t } = useTranslation('components')
  const navigate = useNavigate()

  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="bg-blue-600 text-white rounded-2xl rounded-br-md px-3 py-2 max-w-[85%]">
          <p className="text-sm">{message.content}</p>
        </div>
      </div>
    )
  }

  const hasConfirmButton = !!message.meta?.confirmAction
  const hasRulePreview = !!message.meta?.rulePreview
  // When a structured preview card is rendered we suppress the
  // text bubble entirely — the card carries all the info and the
  // "Preview: Create governance rule" + key/value list above it
  // would just duplicate what the card already shows.
  const visibleContent = hasRulePreview
    ? ''
    : hasConfirmButton
      ? stripConfirmHint(message.content)
      : message.content

  return (
    <div className="space-y-2">
      {visibleContent && (
        <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl rounded-bl-md px-3 py-2 max-w-[95%]">
          <div className="text-sm leading-relaxed whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: simpleMarkdown(visibleContent) }} />
        </div>
      )}
      {message.meta?.rulePreview && (
        <ChatRulePreview rule={message.meta.rulePreview} labelCatalog={labelCatalog} />
      )}

      {message.meta?.confirmAction && onConfirmAction && (
        <Button
          variant={message.meta.confirmAction.style === 'danger' ? 'destructive' : 'default'}
          size="sm"
          onClick={() =>
            onConfirmAction(
              message.meta!.confirmAction!.tool,
              message.meta!.confirmAction!.arguments,
            )
          }
          className="text-xs"
        >
          {message.meta.confirmAction.label}
        </Button>
      )}

      {message.meta?.navigateTo && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(message.meta!.navigateTo!)}
          className={cn(
            'text-xs gap-1.5',
            'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
            'hover:bg-blue-100 dark:hover:bg-blue-900/30',
          )}
        >
          <ArrowUpRight className="w-3 h-3" />
          {t('assistant.actions.viewInAdmin')}
        </Button>
      )}
    </div>
  )
}

// stripConfirmHint removes the paste-able CLI fallback line from
// the preview text when the chat is rendering an actual button —
// the hint is noise in that case and clutters the bubble.
function stripConfirmHint(text: string): string {
  return text
    .split('\n')
    .filter(line => !/^Hint: Click the \*\*/i.test(line.trim()))
    .join('\n')
    .trimEnd()
}
